import { useState } from 'react';
import { Dialog, MainButton, OptionRow, useToast } from '../components';
import { useRenameCategory, useUpdateItem } from '../api/queries';
import { useSurface } from '../hooks/useSurface';
import { ApiError } from '../api/client';
import type { Item } from '../api/types';

/**
 * Dialogs that act on a single item — `ListDialogs.tsx`'s sibling.
 *
 * These are *not* routes, unlike the list dialogs. A list dialog needs its own
 * URL because its back target is a different screen; an item dialog is opened
 * from the row's ⋯ sheet and closes back onto the same screen, so the item it
 * edits is state DetailScreen already holds. Routing it would mean putting an
 * item id in the path and re-resolving it on every render for no gain.
 */

export function EditItemDialog({
  listId,
  item,
  onClose,
}: {
  listId: number;
  item: Item;
  onClose: () => void;
}) {
  const { showToast } = useToast();
  const { isTelegram } = useSurface();
  const update = useUpdateItem(listId);
  // Seeded lazily rather than by effect: `item` is refreshed by the invalidation
  // that follows every write on this list, and an effect syncing it back into
  // the field would overwrite whatever the user had typed by then.
  const [text, setText] = useState<string | null>(null);
  const value = text ?? item.text;

  const submit = () => {
    const trimmed = value.trim();
    // Convenience, not enforcement — `service.set_text` raises on empty text and
    // the route turns that into a 400. The Save button is disabled on empty too;
    // this guard is for the Enter key, which does not consult it.
    if (!trimmed) return;
    // An untouched field has nothing to send. Closing is the honest answer.
    if (trimmed === item.text) {
      onClose();
      return;
    }
    update.mutate(
      { itemId: item.id, text: trimmed },
      {
        onSuccess: () => {
          showToast('Item updated');
          onClose();
        },
      },
    );
  };

  return (
    <>
      <Dialog open onDismiss={onClose} labelledBy="dialog-edititem">
        <div className="dialog__pad">
          <div className="dialog__title" id="dialog-edititem" style={{ textAlign: 'center' }}>
            Edit item
          </div>
          <input
            className="dialog__field"
            value={value}
            autoFocus
            aria-label="Item text"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
          />
          {update.isError ? <div className="dialog__error">{errorText(update.error)}</div> : null}
          <div className="dialog__actions">
            <button type="button" className="dialog__btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="dialog__btn dialog__btn--primary"
              onClick={submit}
              disabled={update.isPending || !value.trim()}
            >
              Save
            </button>
          </div>
        </div>
      </Dialog>
      {isTelegram ? <MainButton label="Save" onClick={submit} loading={update.isPending} /> : null}
    </>
  );
}

/**
 * The "New category…" row, as a choice value. `null` cannot stand in for it —
 * that is already the *other* explicit choice, "No category".
 */
const NEW = Symbol('new category');
type Choice = string | null | typeof NEW;

/** `service.clean_category`'s client half: what the server will store. */
function cleanCategory(raw: string): string | null {
  // The leading `#` is stripped because the add bar taught `#tag`, so it is the
  // spelling people reach for here too — and the server drops it either way.
  return raw.trim().replace(/^#+/, '').trim() || null;
}

/**
 * Move an item into another category, or out of all of them.
 *
 * The choices are the categories already on this list plus a free-text row, not
 * an endpoint's worth of taxonomy: `categoriesOf()` derives them from the items
 * the screen has loaded, and the note there says why that is the whole design.
 *
 * **One frame of the wrong spelling is expected.** `service.set_category` runs
 * the value through `_canonical_category`, which reuses the list's existing
 * spelling — type `food` on a list that already has `Food` and the row comes
 * back as `Food`. The optimistic write shows what was typed and the invalidate
 * in `onSettled` corrects it, which is the same bargain `parseItemPreview`
 * documents for the optimistic add: there is only one writer, so a mismatch
 * costs a frame of a wrongly-labelled row, never a wrongly-stored one.
 */
export function SetCategoryDialog({
  listId,
  item,
  categories,
  onClose,
}: {
  listId: number;
  item: Item;
  categories: string[];
  onClose: () => void;
}) {
  const { showToast } = useToast();
  const { isTelegram } = useSurface();
  const update = useUpdateItem(listId);
  // `undefined` is "untouched", which is why this is not the `?? item.category`
  // idiom the list dialogs use: `null` is a choice here, and `??` would read it
  // as no choice at all and re-seed from the item on every render.
  const [pick, setPick] = useState<Choice | undefined>(undefined);
  const [draft, setDraft] = useState('');
  const choice = pick === undefined ? item.category : pick;
  const typed = cleanCategory(draft);
  const next = choice === NEW ? typed : choice;

  const submit = () => {
    // An empty free-text row has nothing to send; the Save button is disabled
    // for it too, but Enter does not consult that.
    if (choice === NEW && !typed) return;
    if (next === item.category) {
      onClose();
      return;
    }
    update.mutate(
      { itemId: item.id, category: next },
      {
        onSuccess: () => {
          showToast(next ? 'Category updated' : 'Category cleared');
          onClose();
        },
      },
    );
  };

  return (
    <>
      <Dialog open onDismiss={onClose} labelledBy="dialog-category">
        <div className="dialog__pad">
          <div className="dialog__title" id="dialog-category" style={{ textAlign: 'center' }}>
            Category
          </div>
          <div className="dialog__options" role="radiogroup" aria-label="Category">
            <OptionRow
              label="No category"
              selected={choice === null}
              onSelect={() => setPick(null)}
            />
            {/* Verbatim: a category's stored spelling is the one the list agreed
                on, and re-casing it here would be a fourth casing rule on top of
                the three the display sites already apply. */}
            {categories.map((category) => (
              <OptionRow
                key={category}
                label={category}
                selected={choice === category}
                onSelect={() => setPick(category)}
              />
            ))}
            <OptionRow
              label="New category…"
              selected={choice === NEW}
              onSelect={() => setPick(NEW)}
            />
          </div>
          {choice === NEW ? (
            <input
              className="dialog__field"
              value={draft}
              autoFocus
              placeholder="Household"
              aria-label="New category"
              // `service.MAX_CATEGORY_LEN`. The server truncates silently, so
              // the field stops rather than accepting text it will cut.
              maxLength={32}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
          ) : null}
          {update.isError ? <div className="dialog__error">{errorText(update.error)}</div> : null}
          <div className="dialog__actions">
            <button type="button" className="dialog__btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="dialog__btn dialog__btn--primary"
              onClick={submit}
              disabled={update.isPending || (choice === NEW && !typed)}
            >
              Save
            </button>
          </div>
        </div>
      </Dialog>
      {isTelegram ? <MainButton label="Save" onClick={submit} loading={update.isPending} /> : null}
    </>
  );
}

/**
 * Rename a category across the whole list, or strip it off every item — the
 * bot's `/cat old | new` and `/cat old |`, which nothing on this surface could
 * do before.
 *
 * Local state rather than a route, unlike the list dialogs. A route would
 * remount `DetailScreen`, and the To do / Done filter it holds is local state
 * that would go with it: opening a rename from a filtered view and coming back
 * to "All" is a worse trade than the URL it would buy.
 *
 * **Renaming into an existing category merges the two sections**, because
 * `service.rename_category` reuses the list's spelling — `food` → `Groceries`
 * on a list that already has `Groceries` files everything under the one that was
 * already there. That is the point, not a caveat, so the copy says "or merge"
 * and the hook does not try to predict the outcome.
 */
export function RenameCategoryDialog({
  listId,
  category,
  onClose,
}: {
  listId: number;
  category: string;
  onClose: () => void;
}) {
  const { showToast } = useToast();
  const { isTelegram } = useSurface();
  const rename = useRenameCategory(listId);
  // Seeded lazily, the way `RenameListDialog` seeds from its query: the list
  // refetches on every write, and an effect syncing that back into the field
  // would overwrite what the user had typed.
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? category;
  const typed = cleanCategory(value);

  const done = (result: { updated: number }, to: string | null) => {
    // Matches the bot's `/cat` replies word for word — the same action reported
    // two ways would read as two different features to someone who uses both.
    showToast(renameToast(category, to, result.updated));
    onClose();
  };

  const submit = () => {
    // Convenience for the Enter key, which does not consult the disabled Save.
    if (!typed) return;
    // Nothing to say to the server, and no toast to earn. Casing alone *is* a
    // change — 'food' -> 'Food' is how the bot re-spells a tag — so this
    // compares exactly rather than case-insensitively.
    if (typed === category) {
      onClose();
      return;
    }
    rename.mutate({ from: category, to: typed }, { onSuccess: (result) => done(result, typed) });
  };

  const clear = () =>
    rename.mutate({ from: category, to: null }, { onSuccess: (result) => done(result, null) });

  return (
    <>
      <Dialog open onDismiss={onClose} labelledBy="dialog-rename-category">
        <div className="dialog__pad">
          <div className="dialog__title" id="dialog-rename-category" style={{ textAlign: 'center' }}>
            Rename category
          </div>
          <input
            className="dialog__field"
            value={value}
            autoFocus
            aria-label="Category name"
            placeholder={category}
            // `service.MAX_CATEGORY_LEN`, as in the picker above: the server
            // truncates silently, so the field stops rather than taking text it
            // will cut.
            maxLength={32}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
          />
          <div className="dialog__hint">
            Renaming into a category this list already has merges the two.
          </div>
          {rename.isError ? <div className="dialog__error">{errorText(rename.error)}</div> : null}
          <div className="dialog__actions">
            <button type="button" className="dialog__btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="dialog__btn dialog__btn--primary"
              onClick={submit}
              disabled={rename.isPending || !typed}
            >
              Save
            </button>
          </div>
          {/* Its own row below the primary pair rather than beside them: it is
              the same verb on the same tag, but it destroys the grouping, so it
              should not sit one mis-tap away from Save. */}
          <div className="dialog__actions dialog__actions--secondary">
            <button
              type="button"
              className="dialog__btn dialog__btn--destructive"
              onClick={clear}
              disabled={rename.isPending}
            >
              Clear this category from all items
            </button>
          </div>
        </div>
      </Dialog>
      {isTelegram ? <MainButton label="Save" onClick={submit} loading={rename.isPending} /> : null}
    </>
  );
}

/** The bot's `/cat` replies, verbatim. `updated === 0` is a success, not a failure. */
function renameToast(from: string, to: string | null, updated: number): string {
  if (updated === 0) return `No items tagged '${from}'`;
  const s = updated === 1 ? '' : 's';
  return to
    ? `Renamed '${from}' → '${to}' on ${updated} item${s}`
    : `Cleared '${from}' from ${updated} item${s}`;
}

function errorText(error: unknown): string {
  // A 403 here means the share was downgraded while the dialog sat open. The
  // row has already snapped back behind it; say why rather than leave the
  // field looking saveable.
  if (error instanceof ApiError && error.isForbidden) {
    return 'You only have view access to this list.';
  }
  return error instanceof Error ? error.message : 'Something went wrong.';
}
